import {
    AnyBookingProduct,
    FerryProduct,
    FlightProduct,
    MyBooking,
    MyBookingSpotlight,
    MyBookingSpotlightHotelWidget,
    MyBookingSpotlightTripWidget,
    ProductType,
    SpotlightType,
} from '@/api/MyBooking/myBooking.interfaces';
import { expiredProduct, getProductToDate } from '@/helpers/myBooking';
import {
    convertToDate,
    getDurationInDays,
    getFormattedDate,
    getShortDate,
    isBefore,
} from '@shared/helpers/datetime/datetime';
import { HotelWidgetData, HotelWidgetSummary } from '../Widgets/Hotel/HotelWidget.interfaces';
import { HOTEL_NUMBER_OF_NIGHTS } from './Spotlight.consts';
import { getAge } from '@/helpers/dobValidation';
import { ProductCombinationType } from '@shared/interfaces/productCombination';
import { trackClarity } from '@/tracking/clarity';
import { ClarityEvent } from '@/tracking/clarity/types';
import { isActiveBooking, isExpiredBooking } from '@/store/myBooking';
import {
    logStatsigBookingSpotlights,
    logStatsigBookingSpotlightsRearranged,
} from '@/tracking/statsig/booking.functions';
import { TripWidgetData, TripWidgetPassengers } from '../Widgets/Trip/TripWidget.interfaces';
import { getSupportedTrip } from '../Widgets/Trip/TripWidget.helpers';
import { consoleLog, LogColorType } from '@shared/helpers/log';
import { endpoints } from '@/api/endpoints';
import { handleCustomError } from '@/config/errorHandling';

// This function exists if we really need "requirements/conditions" to verify if a widget can be shown.
// HotelWidget example below could potentially be removed if we want to show it in a booking that already has hotels
// We also run the createSpotlights below based on what the booking products.
function isSpotlightEnabled(type: SpotlightType, booking: MyBooking): boolean {
    if (type === SpotlightType.HotelWidget) {
        return Boolean(!booking.productCombination?.includes.hotel);
    }

    if (type === SpotlightType.RentalCarWidget) {
        return true;
    }

    if (type === SpotlightType.TripWidget) {
        return true;
    }

    if (type === SpotlightType.Upsells) {
        return true;
    }

    return false;
}

function createSpotlightHotelPlus(booking: MyBooking): MyBookingSpotlight[] {
    return [
        {
            position: 1,
            type: SpotlightType.Upsells,
            enabled: isSpotlightEnabled(SpotlightType.Upsells, booking),
        },
        {
            position: 2,
            type: SpotlightType.RentalCarWidget,
            enabled: isSpotlightEnabled(SpotlightType.RentalCarWidget, booking),
        },
    ];
}

async function createSpotlightFlightOnly(booking: MyBooking): Promise<MyBookingSpotlight[]> {
    const products = booking.products.filter((x) => x.type === ProductType.Flight);

    const product = products[0] as FlightProduct;

    const tripWidgetData: TripWidgetData | null = getTripWidgetData(product);
    const hotelWidgetData: HotelWidgetData | null = await getHotelWidgetData(
        product,
        booking.bookingId
    );

    const productIsExpired = expiredProduct(product);

    if (tripWidgetData) {
        return [
            {
                position: 1,
                type: SpotlightType.TripWidget,
                data: tripWidgetData,
                enabled:
                    !productIsExpired &&
                    isSpotlightEnabled(SpotlightType.TripWidget, booking),
            },
            {
                position: 2,
                type: SpotlightType.RentalCarWidget,
                enabled: isSpotlightEnabled(SpotlightType.RentalCarWidget, booking),
            },
            {
                position: productIsExpired ? 1 : 3,
                type: SpotlightType.Upsells,
                enabled: isSpotlightEnabled(SpotlightType.Upsells, booking),
            },
        ];
    } else if (hotelWidgetData) {
        return [
            {
                position: 1,
                type: SpotlightType.HotelWidget,
                data: hotelWidgetData,
                enabled:
                    !productIsExpired &&
                    isSpotlightEnabled(SpotlightType.HotelWidget, booking),
            },
            {
                position: 2,
                type: SpotlightType.RentalCarWidget,
                enabled: isSpotlightEnabled(SpotlightType.RentalCarWidget, booking),
            },
            {
                position: 3,
                type: SpotlightType.Upsells,
                enabled: isSpotlightEnabled(SpotlightType.Upsells, booking),
            },
        ];
    } else {
        return [
            {
                position: 1,
                type: SpotlightType.Upsells,
                enabled: isSpotlightEnabled(SpotlightType.Upsells, booking),
            },
            {
                position: 2,
                type: SpotlightType.RentalCarWidget,
                enabled: isSpotlightEnabled(SpotlightType.RentalCarWidget, booking),
            },
        ];
    }
}

async function createSpotlightFerryOnly(booking: MyBooking): Promise<MyBookingSpotlight[]> {
    const products = booking.products.filter((x) => x.type === ProductType.Ferry);

    const product = products[0] as FerryProduct;

    const hotelWidgetData: HotelWidgetData | null = await getHotelWidgetData(
        product,
        booking.bookingId
    );

    const productIsExpired = expiredProduct(product);

    if (hotelWidgetData) {
        return [
            {
                position: 1,
                type: SpotlightType.Upsells,
                enabled: isSpotlightEnabled(SpotlightType.Upsells, booking),
            },
            {
                position: 2,
                type: SpotlightType.HotelWidget,
                data: hotelWidgetData,
                enabled:
                    !productIsExpired &&
                    isSpotlightEnabled(SpotlightType.HotelWidget, booking),
            },
            {
                position: 3,
                type: SpotlightType.RentalCarWidget,
                enabled: isSpotlightEnabled(SpotlightType.RentalCarWidget, booking),
            },
        ];
    } else {
        return [
            {
                position: 1,
                type: SpotlightType.Upsells,
                enabled: isSpotlightEnabled(SpotlightType.Upsells, booking),
            },
            {
                position: 2,
                type: SpotlightType.RentalCarWidget,
                enabled: isSpotlightEnabled(SpotlightType.RentalCarWidget, booking),
            },
        ];
    }
}

function sortSpotlights(spotlights: MyBookingSpotlight[]) {
    const ranked: MyBookingSpotlight[] = [];
    const positions = new Set<number>();

    spotlights.forEach((spotlight) => {
        if (spotlight.enabled && !positions.has(spotlight.position)) {
            // Try to add the enabled spotlight at its original position
            ranked[spotlight.position - 1] = spotlight;
            positions.add(spotlight.position);
        }
    });

    for (let i = 0; i < ranked.length; i++) {
        if (!ranked[i]) {
            const nextAvailable = spotlights.find(
                (s) => s.enabled && !positions.has(s.position)
            );

            if (nextAvailable) {
                ranked[i] = nextAvailable;
                positions.add(nextAvailable.position);
            }
        }
    }

    if (spotlights.length !== ranked.length) {
        const removedTypes = spotlights
            .filter((item) => !ranked.includes(item))
            .map((x) => x.type);

        consoleLog(
            LogColorType.Spotlights,
            'sortSpotlights(): Removed following:',
            removedTypes
        );

        logStatsigBookingSpotlightsRearranged(removedTypes);
        trackClarity(ClarityEvent.MY_BOOKING_SPOTLIGHTS_REARRANGE);
    }

    // Remove any empty slots and adjust position numbering
    return ranked.filter(Boolean).map((spotlight, index) => ({
        ...spotlight,
        position: index + 1,
    }));
}

export async function createSpotlights(booking: MyBooking): Promise<MyBookingSpotlight[]> {
    let available: MyBookingSpotlight[] = [];

    const isExpired = isExpiredBooking(booking);
    const isActive = isActiveBooking(booking);

    // Do not show any "upsell"
    if (isExpired || !isActive) {
        return [];
    }

    switch (booking.productCombination?.type) {
        case ProductCombinationType.Flight: {
            available = await createSpotlightFlightOnly(booking);
            break;
        }
        case ProductCombinationType.Ferry: {
            available = await createSpotlightFerryOnly(booking);
            break;
        }
        case ProductCombinationType.HotelFlight:
        case ProductCombinationType.HotelFerry:
        case ProductCombinationType.HotelFlightActivity:
        case ProductCombinationType.HotelFerryActivity: {
            available = createSpotlightHotelPlus(booking);
            break;
        }
        default: {
            available = [
                {
                    position: 1,
                    type: SpotlightType.Upsells,
                    enabled: isSpotlightEnabled(SpotlightType.Upsells, booking),
                },
            ];
            break;
        }
    }

    const spotlights = sortSpotlights(available);
    logStatsigBookingSpotlights(spotlights);

    return spotlights;
}

function getTripWidgetData(product: AnyBookingProduct): TripWidgetData | null {
    switch (product?.type) {
        case ProductType.Flight: {
            return getTripWidgetFlightData(product);
        }
        default:
            return null;
    }
}

function getTripWidgetFlightData(product: FlightProduct): TripWidgetData | null {
    const [outbound, inbound] = product.legs;

    if (outbound && outbound.segments) {
        const outboundSegment = outbound.segments[outbound.segments.length - 1];

        if (outboundSegment && outboundSegment.arrivalDate) {
            let days = undefined;

            if (inbound) {
                const inboundSegment = inbound.segments[0];

                if (inboundSegment?.departureDate) {
                    days =
                        getDurationInDays(
                            convertToDate(outboundSegment.arrivalDate),
                            convertToDate(inboundSegment.departureDate)
                        ) - 1; // Subtract one day
                }
            }

            const trip = getSupportedTrip(outboundSegment.arrivalAirportCode, days);

            if (trip) {
                return {
                    from: outboundSegment.arrivalDate,
                    passengers: getTripPassengersByFlight(product),
                    trip,
                } as TripWidgetData;
            }
        }
    }

    return null;
}

function getTripPassengersByFlight(product: FlightProduct): TripWidgetPassengers {
    if (!product) {
        // Default
        return {
            adults: 1,
            children: [],
        };
    }

    const ages = product.pax.map((x) => {
        const toDate = getProductToDate(product);

        if (toDate && x.dateOfBirth) {
            return getAge(getFormattedDate(new Date(x.dateOfBirth)), toDate);
        }

        return 99;
    });

    const adults = ages.filter((x) => x >= 18);
    const children = ages.filter((x) => x < 18);

    return {
        adults: adults.length,
        children,
    };
}

async function getHotelWidgetData(
    product: AnyBookingProduct,
    bookingId: string
): Promise<HotelWidgetData | null> {
    switch (product?.type) {
        case ProductType.Flight: {
            return getHotelWidgetFlightData(product, bookingId);
        }
        case ProductType.Ferry: {
            return getHotelWidgetFerryData(product, bookingId);
        }
        default:
            return null;
    }
}

async function getHotelWidgetFlightData(
    product: FlightProduct,
    bookingId: string
): Promise<HotelWidgetData | null> {
    if (product) {
        const leg = product.legs[0];
        const segment = leg.segments[leg.segments.length - 1];

        const arrivalLabel = `${segment.arrivalCityName} ${segment.arrivalCountryName}`;
        const suggestion = await endpoints.content
            .GetSuggestionByTerm(arrivalLabel)
            .catch((error) => {
                handleCustomError(`GetCategorizedSuggestions ${error}`);
            });
        const destination = suggestion && suggestion.label ? suggestion.label : arrivalLabel;
        const polygonId = suggestion?.polygonId ?? null;

        if (product && segment.arrivalCityName && segment.arrivalDate) {
            return {
                type: ProductType.Flight,
                city: segment.arrivalCityName,
                destination,
                polygonId,
                summary: getHotelSummaryBasedOnTransport(product),
                date:
                    getShortDate(convertToDate(segment.arrivalDate)) ??
                    getShortDate(new Date()),
                bookingId,
            };
        }
    }

    return null;
}

async function getHotelWidgetFerryData(
    product: FerryProduct,
    bookingId: string
): Promise<HotelWidgetData | null> {
    if (product) {
        const leg = product.legs[0];
        const segment = leg.segments[leg.segments.length - 1];

        const arrivalLabel = `${segment.arrivalPortName} ${segment.arrivalPortLocationName}`;
        const suggestion = await endpoints.content
            .GetSuggestionByTerm(arrivalLabel)
            .catch((error) => {
                handleCustomError(`GetCategorizedSuggestions ${error}`);
            });
        const destination = suggestion && suggestion.label ? suggestion.label : arrivalLabel;
        const polygonId = suggestion?.polygonId ?? null;

        if (product && segment.arrivalPortLocationName && segment.arrivalDate) {
            return {
                type: ProductType.Ferry,
                city: segment.arrivalPortLocationName,
                destination,
                polygonId,
                summary: getHotelSummaryBasedOnTransport(product),
                date:
                    getShortDate(convertToDate(segment.arrivalDate)) ??
                    getShortDate(new Date()),
                bookingId,
            };
        }
    }

    return null;
}

function getHotelSummaryBasedOnTransport(
    product: FlightProduct | FerryProduct
): HotelWidgetSummary {
    if (!product) {
        // Default
        return {
            nights: HOTEL_NUMBER_OF_NIGHTS,
            adults: 1,
            children: [],
        };
    }

    const ages = product.pax.map((x) => {
        const toDate = getProductToDate(product);

        if (toDate && x.dateOfBirth) {
            return getAge(getFormattedDate(new Date(x.dateOfBirth)), toDate);
        }

        return 99;
    });

    const adults = ages.filter((x) => x >= 18);
    const children = ages.filter((x) => x < 18);

    return {
        nights: HOTEL_NUMBER_OF_NIGHTS,
        adults: adults.length,
        children,
    };
}

export function getSpotlightHotelWidgetData(spotlight: MyBookingSpotlight) {
    const hotelSpotlight = spotlight as MyBookingSpotlightHotelWidget;
    const today = convertToDate(getShortDate(new Date()));
    const date = convertToDate(hotelSpotlight.data.date);

    return isBefore(date, today)
        ? undefined
        : {
              type: SpotlightType.HotelWidget,
              props: { data: hotelSpotlight.data },
          };
}

export function getSpotlightTripWidgetData(spotlight: MyBookingSpotlight) {
    const tripSpotlight = spotlight as MyBookingSpotlightTripWidget;

    return {
        type: SpotlightType.TripWidget,
        props: { data: tripSpotlight.data },
    };
}

export function getSpotlightRentalCarWidgetData() {
    return { type: SpotlightType.RentalCarWidget };
}

export function getSpotlightUpsellsData() {
    return { type: SpotlightType.Upsells };
}
